/**
 * Login page: "Sign in with Google" redirects to web-backend auth URL,
 * which redirects to Google OAuth (redirect_uri = /web/admin/auth/callback).
 * On success, callback redirects to admin portal (default /workspaces). On error (e.g. Access denied),
 * callback redirects to admin portal with ?error=... which we display here.
 */
import { useSearchParams } from 'react-router-dom';
import { getLoginUrl, isValidReturnPath } from '../api/client';

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const errorFromUrl = searchParams.get('error');
  const returnTo = searchParams.get('returnTo');
  const safeReturnTo =
    returnTo && isValidReturnPath(returnTo) ? returnTo : '/workspaces';
  const loginUrl = getLoginUrl(safeReturnTo);

  // Only show the link when we have an absolute API URL; otherwise user would stay on same origin and see no redirect
  const isAbsoluteApiUrl = loginUrl.startsWith('http://') || loginUrl.startsWith('https://');

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Admin Portal</h1>
        <p style={styles.subtitle}>Sign in to view client workspaces.</p>
        {errorFromUrl && (
          <div style={styles.error} role="alert">
            <p style={styles.errorTitle}>Sign-in failed</p>
            <p style={styles.errorText}>{errorFromUrl}</p>
          </div>
        )}
        {isAbsoluteApiUrl ? (
          <a
            href={loginUrl}
            style={styles.button}
            rel="noopener noreferrer"
            target="_self"
            data-testid="login-link"
          >
            Sign in with Google
          </a>
        ) : (
          <div style={styles.error}>
            <p style={styles.errorTitle}>API not configured</p>
            <p style={styles.errorText}>
              This build was not made with an API base URL. Deploy using{' '}
              <code style={styles.code}>./scripts/deploy.sh stg</code> so{' '}
              <code style={styles.code}>VITE_API_BASE</code> is set (e.g.
              https://api.stg.acktracked.com). Otherwise the login link goes to this host and no
              request reaches the API.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    background: '#fff',
    padding: 40,
    borderRadius: 12,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    maxWidth: 400,
    width: '100%',
    textAlign: 'center',
  },
  title: {
    margin: '0 0 8px',
    fontSize: 24,
    fontWeight: 600,
    color: '#1a1a1a',
  },
  subtitle: {
    margin: '0 0 24px',
    fontSize: 14,
    color: '#6b7280',
  },
  button: {
    display: 'inline-block',
    padding: '12px 24px',
    background: '#2563eb',
    color: '#fff',
    borderRadius: 8,
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'none',
    fontSize: 16,
  },
  error: {
    textAlign: 'left' as const,
    padding: '16px 0 0',
    borderTop: '1px solid #e5e7eb',
  },
  errorTitle: {
    margin: '0 0 8px',
    fontSize: 14,
    fontWeight: 600,
    color: '#b91c1c',
  },
  errorText: {
    margin: 0,
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 1.5,
  },
  code: {
    padding: '2px 6px',
    background: '#f3f4f6',
    borderRadius: 4,
    fontSize: 12,
  },
};
